import {
  calcRoom,
  formatQuantity,
  layoutRoom,
  wallAreas,
  type LayoutWall,
  type MeasureRoom,
  type PlacedOpening,
  type RoomLayout,
} from '@erp/shared';

/**
 * Схемы помещения по обмерам: план сверху и развёртка стен. Показываются
 * прорабу в редакторе обмеров и заказчику в печатной форме сметы — заказчик
 * видит, откуда взялись метры в строках сметы.
 *
 * Цвета заданы литералами, а не CSS-переменными темы: печатная форма
 * собирается в PDF через html2canvas, а он переменные внутри SVG не разрешает.
 */
export const INK = '#2e2924';
export const DIM = '#7d7568';
export const FLOOR = '#f7f5f2';
const WALL_FILL = '#ece8e2';
const GLASS = '#dbe8f3';
const GLASS_LINE = '#4a7aa8';
export const CONFLICT = '#c4432a';

export const FONT = 'IBM Plex Sans, Arial, sans-serif';

/** Толщина стены на плане, px. Условная: реальная толщина в обмерах не нужна. */
const WALL_PX = 6;

export function fmtMeters(value: number): string {
  return formatQuantity(value.toFixed(3));
}

export type Point = [number, number];

/** Единичный вектор направления стены; ось y вниз, как на экране. */
function unit(direction: number): Point {
  const rad = (direction * Math.PI) / 180;
  return [Math.cos(rad), Math.sin(rad)];
}

function pointInPolygon([px, py]: Point, poly: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]!;
    const [xj, yj] = poly[j]!;
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function distanceToSegment([px, py]: Point, [ax, ay]: Point, [bx, by]: Point): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/**
 * Точка для подписи помещения — самая удалённая от стен. Центр габарита не
 * годится: у Г-образной комнаты он попадает в вырезанный угол, за стену.
 */
export function labelPoint(poly: Point[]): { point: Point; clearance: number } | null {
  const xs = poly.map((p) => p[0]);
  const ys = poly.map((p) => p[1]);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const steps = 24;
  let best: { point: Point; clearance: number } | null = null;
  for (let i = 1; i < steps; i++) {
    for (let j = 1; j < steps; j++) {
      const p: Point = [minX + ((maxX - minX) * i) / steps, minY + ((maxY - minY) * j) / steps];
      if (!pointInPolygon(p, poly)) continue;
      let clearance = Infinity;
      for (let k = 0; k < poly.length; k++) {
        clearance = Math.min(clearance, distanceToSegment(p, poly[k]!, poly[(k + 1) % poly.length]!));
      }
      if (!best || clearance > best.clearance) best = { point: p, clearance };
    }
  }
  return best;
}

function PlanOpening({ opening, scale }: { opening: PlacedOpening; scale: number }) {
  const x = opening.start * scale;
  const len = opening.width * scale;
  const t = WALL_PX;
  const line = opening.conflict ? CONFLICT : opening.kind === 'WINDOW' ? GLASS_LINE : INK;

  return (
    <g transform={`translate(${x} 0)`}>
      {/* Проём вырезает стену: заливаем его цветом пола (дверь) или стекла (окно). */}
      <rect x={0} y={-t - 0.5} width={len} height={t + 1} fill={opening.kind === 'WINDOW' ? GLASS : FLOOR} />
      <line x1={0} y1={-t} x2={0} y2={0} stroke={line} strokeWidth={1} />
      <line x1={len} y1={-t} x2={len} y2={0} stroke={line} strokeWidth={1} />
      {opening.kind === 'WINDOW' ? (
        <>
          <line x1={0} y1={-t} x2={len} y2={-t} stroke={line} strokeWidth={1} />
          <line x1={0} y1={-t / 2} x2={len} y2={-t / 2} stroke={line} strokeWidth={1} />
          <line x1={0} y1={0} x2={len} y2={0} stroke={line} strokeWidth={1} />
        </>
      ) : (
        <>
          {/* Полотно открыто на 90° внутрь помещения, дуга — траектория открывания. */}
          <line x1={0} y1={0} x2={0} y2={len} stroke={line} strokeWidth={1.5} />
          <path
            d={`M ${len} 0 A ${len} ${len} 0 0 1 0 ${len}`}
            fill="none"
            stroke={line}
            strokeWidth={0.75}
            strokeDasharray="3 2"
          />
        </>
      )}
    </g>
  );
}

export function WallBadge({ x, y, wall }: { x: number; y: number; wall: number }) {
  return (
    <g>
      <circle cx={x} cy={y} r={7} fill="#fff" stroke={DIM} strokeWidth={0.75} />
      <text x={x} y={y + 3.5} textAnchor="middle" fontSize={9} fontFamily={FONT} fill={DIM}>
        {wall}
      </text>
    </g>
  );
}

/** Поворот подписи вдоль стены так, чтобы текст не читался вверх ногами. */
function readableAngle(direction: number): number {
  return direction > 90 && direction <= 270 ? direction - 180 : direction;
}

const PLAN_MAX_W = 340;
/**
 * Одна штриховка на страницу: id в SVG глобален, а одинаковые паттерны в
 * нескольких планах друг другу не мешают — все ссылки ведут на идентичный.
 */
const SLOPE_HATCH_ID = 'erp-slope-hatch';
const PLAN_MAX_H = 260;
const PLAN_PAD = 30;

/** План помещения сверху: стены по контуру, окна, двери с открыванием, длины стен. */
export function RoomPlan({ room, layout: given }: { room: MeasureRoom; layout?: RoomLayout | null }) {
  const layout = given === undefined ? layoutRoom(room) : given;
  if (!layout || layout.walls.length === 0) return null;

  const points: Point[] = layout.walls.map((w) => [w.x1, w.y1]);
  const last = layout.walls.at(-1)!;
  const end: Point = [last.x2, last.y2];
  const all = [...points, end];
  const minX = Math.min(...all.map((p) => p[0]));
  const maxX = Math.max(...all.map((p) => p[0]));
  const minY = Math.min(...all.map((p) => p[1]));
  const maxY = Math.max(...all.map((p) => p[1]));
  const scale = Math.min(PLAN_MAX_W / Math.max(maxX - minX, 0.5), PLAN_MAX_H / Math.max(maxY - minY, 0.5));
  const toPx = ([x, y]: Point): Point => [PLAN_PAD + (x - minX) * scale, PLAN_PAD + (y - minY) * scale];
  const vbW = PLAN_PAD * 2 + (maxX - minX) * scale;
  const vbH = PLAN_PAD * 2 + (maxY - minY) * scale;
  const t = WALL_PX;
  // Внутренняя сторона стены — справа по ходу обхода у контура по часовой стрелке.
  const side = layout.clockwise ? 1 : -1;

  const px = points.map(toPx);
  const endPx = toPx(end);
  const polygon = px.map(([x, y]) => `${x},${y}`).join(' ');
  const wallsPath =
    `M ${px.map(([x, y]) => `${x} ${y}`).join(' L ')}` + (layout.closed ? ' Z' : ` L ${endPx[0]} ${endPx[1]}`);

  const label = labelPoint(points);
  const slopedParts = layout.ceiling.filter((part) => part.sloped);
  const legendH = slopedParts.length > 0 ? 16 : 0;

  return (
    <svg
      viewBox={`0 0 ${vbW} ${vbH + legendH}`}
      width="100%"
      style={{ maxWidth: vbW, display: 'block' }}
      role="img"
      aria-label={`План помещения «${room.name}»`}
    >
      <defs>
        <pattern id={SLOPE_HATCH_ID} width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1={0} y1={0} x2={0} y2={6} stroke={DIM} strokeWidth={0.6} />
        </pattern>
      </defs>
      {/* Стена рисуется линией двойной толщины по контуру, а пол закрывает её
          внутреннюю половину: размеры в обмерах внутренние, стена — снаружи. */}
      <path d={wallsPath} fill="none" stroke={INK} strokeWidth={t * 2} strokeLinejoin="miter" />
      <polygon points={polygon} fill={FLOOR} />
      {/* Скаты потолка — штриховкой по полу под ними; граница — где начинается плоский потолок. */}
      {slopedParts.map((part, i) => (
        <polygon
          key={i}
          points={part.polygon.map((p) => toPx(p).join(',')).join(' ')}
          fill={`url(#${SLOPE_HATCH_ID})`}
          stroke={DIM}
          strokeWidth={0.75}
          strokeDasharray="4 3"
        />
      ))}
      {slopedParts.length > 0 && (
        <g transform={`translate(${PLAN_PAD - 20} ${vbH + 4})`}>
          <rect x={0} y={0} width={14} height={9} fill={`url(#${SLOPE_HATCH_ID})`} stroke={DIM} strokeWidth={0.75} />
          <text x={19} y={8} fontSize={9} fontFamily={FONT} fill={DIM}>
            скат потолка
          </text>
        </g>
      )}
      {!layout.closed && (
        <line
          x1={endPx[0]}
          y1={endPx[1]}
          x2={px[0]![0]}
          y2={px[0]![1]}
          stroke={CONFLICT}
          strokeWidth={WALL_PX}
          strokeDasharray="6 4"
        />
      )}

      {layout.openings.map((o, i) => {
        const wall = layout.walls[o.wall - 1]!;
        const [sx, sy] = toPx([wall.x1, wall.y1]);
        return (
          <g key={i} transform={`translate(${sx} ${sy}) rotate(${wall.direction}) scale(1 ${side})`}>
            <PlanOpening opening={o} scale={scale} />
          </g>
        );
      })}

      {layout.walls.map((wall) => (
        <WallMarks key={wall.number} wall={wall} toPx={toPx} scale={scale} side={side} />
      ))}

      {label && label.clearance * scale >= 22 && (
        <text
          x={toPx(label.point)[0]}
          y={toPx(label.point)[1] - 2}
          textAnchor="middle"
          fontFamily={FONT}
          fill={INK}
        >
          <tspan fontSize={12} fontWeight={600}>
            {room.name}
          </tspan>
          <tspan x={toPx(label.point)[0]} dy={15} fontSize={11} fill={DIM}>
            {formatQuantity(calcRoom(room).FLOOR_AREA)} м²
          </tspan>
        </text>
      )}
    </svg>
  );
}

/** Длина стены снаружи и её номер внутри помещения. */
function WallMarks({
  wall,
  toPx,
  scale,
  side,
}: {
  wall: LayoutWall;
  toPx: (p: Point) => Point;
  scale: number;
  side: number;
}) {
  const [ux, uy] = unit(wall.direction);
  // Внутренняя нормаль — поворот направления на 90° по часовой стрелке на экране.
  const inward: Point = [-uy * side, ux * side];
  const [mx, my] = toPx([(wall.x1 + wall.x2) / 2, (wall.y1 + wall.y2) / 2]);
  const lengthPx = wall.length * scale;
  const out = WALL_PX + 10;
  const lx = mx - inward[0] * out;
  const ly = my - inward[1] * out;

  return (
    <g>
      {lengthPx >= 22 && (
        <text
          x={lx}
          y={ly}
          transform={`rotate(${readableAngle(wall.direction)} ${lx} ${ly})`}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={10}
          fontFamily={FONT}
          fill={DIM}
        >
          {fmtMeters(wall.length)}
        </text>
      )}
      {lengthPx >= 26 && <WallBadge wall={wall.number} x={mx + inward[0] * 12} y={my + inward[1] * 12} />}
    </g>
  );
}

const UNFOLD_MAX_WALL_W = 280;
const UNFOLD_MAX_H = 130;
const UNFOLD_PAD = { top: 8, side: 8, bottom: 34 };

function ElevationOpening({ opening, scale, wallH }: { opening: PlacedOpening; scale: number; wallH: number }) {
  const x = UNFOLD_PAD.side + opening.start * scale;
  const w = opening.width * scale;
  const h = opening.height * scale;
  const y = UNFOLD_PAD.top + wallH - (opening.bottom + opening.height) * scale;
  const stroke = opening.conflict ? CONFLICT : opening.kind === 'WINDOW' ? GLASS_LINE : INK;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill={opening.kind === 'WINDOW' ? GLASS : FLOOR}
        stroke={stroke}
        strokeWidth={opening.conflict ? 1.5 : 1}
      />
      {opening.kind === 'WINDOW' ? (
        // Импост посередине — чтобы окно читалось как окно, а не как ниша.
        <line x1={x + w / 2} y1={y} x2={x + w / 2} y2={y + h} stroke={stroke} strokeWidth={0.75} />
      ) : (
        <circle cx={x + w - Math.min(6, w / 4)} cy={y + h * 0.52} r={1.6} fill={stroke} />
      )}
      {w > 44 && h > 18 && (
        <text
          x={x + w / 2}
          y={y + h / 2 + 3}
          textAnchor="middle"
          fontSize={9}
          fontFamily={FONT}
          fill={stroke}
          paintOrder="stroke"
          stroke={opening.kind === 'WINDOW' ? GLASS : FLOOR}
          strokeWidth={3}
        >
          {fmtMeters(opening.width)}×{fmtMeters(opening.height)}
        </text>
      )}
    </g>
  );
}

/** Контур стены на развёртке: пол и линия потолка над ней. */
function wallOutline(wall: LayoutWall, scale: number, floorY: number): string {
  const x = (t: number) => UNFOLD_PAD.side + t * scale;
  const top = wall.profile.map(([t, h]) => `${x(t)},${floorY - h * scale}`);
  return [...top, `${x(wall.length)},${floorY}`, `${x(0)},${floorY}`].join(' ');
}

/** Подпись размера: у прямоугольной стены «длина×высота», у стены под скатом — диапазон высот. */
function wallSize(wall: LayoutWall): string {
  const heights = wall.profile.map(([, h]) => h);
  const [low, high] = [Math.min(...heights), Math.max(...heights)];
  if (high - low < 1e-6) return `${fmtMeters(wall.length)}×${fmtMeters(high)} м`;
  return `${fmtMeters(wall.length)} м, выс. ${fmtMeters(low)}–${fmtMeters(high)} м`;
}

/**
 * Развёртка: стены в порядке обхода по часовой стрелке, каждая — как её
 * видно изнутри помещения. Масштаб у всех стен общий, иначе короткая стена
 * выглядела бы такой же, как длинная.
 */
export function WallUnfold({ room, layout: given }: { room: MeasureRoom; layout?: RoomLayout | null }) {
  const layout = given === undefined ? layoutRoom(room) : given;
  const areas = wallAreas(room);
  if (!layout || !areas || layout.height <= 0) return null;

  const longest = Math.max(...layout.walls.map((w) => w.length), 0.5);
  const scale = Math.min(UNFOLD_MAX_WALL_W / longest, UNFOLD_MAX_H / layout.height);
  // Высота рисунка у всех стен общая — по высоте помещения: стена под скатом
  // выглядит низкой рядом с торцевой, как в жизни.
  const wallH = layout.height * scale;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
      {layout.walls.map((wall) => {
        const wallW = wall.length * scale;
        const vbW = UNFOLD_PAD.side * 2 + Math.max(wallW, 130);
        const vbH = UNFOLD_PAD.top + wallH + UNFOLD_PAD.bottom;
        const floorY = UNFOLD_PAD.top + wallH;
        return (
          <svg
            key={wall.number}
            viewBox={`0 0 ${vbW} ${vbH}`}
            width={vbW}
            style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
            role="img"
            aria-label={`Стена ${wall.number}`}
          >
            <polygon points={wallOutline(wall, scale, floorY)} fill={WALL_FILL} stroke={INK} />
            {layout.openings
              .filter((o) => o.wall === wall.number)
              .map((o, i) => (
                <ElevationOpening key={i} opening={o} scale={scale} wallH={wallH} />
              ))}
            <line
              x1={UNFOLD_PAD.side - 4}
              y1={floorY}
              x2={UNFOLD_PAD.side + wallW + 4}
              y2={floorY}
              stroke={INK}
              strokeWidth={2}
            />
            <WallBadge wall={wall.number} x={UNFOLD_PAD.side + 7} y={floorY + 16} />
            <text x={UNFOLD_PAD.side + 19} y={floorY + 19.5} fontSize={10} fontFamily={FONT} fill={INK}>
              <tspan fontWeight={600}>{wallSize(wall)}</tspan>
              <tspan fill={DIM}> · {formatQuantity(areas[wall.number - 1]!)} м²</tspan>
            </text>
          </svg>
        );
      })}
    </div>
  );
}
